import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { api } from "@/lib/api";
import { TourCard } from "@/components/site/TourCard";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES, DESTINATIONS } from "@/lib/images";

const CATS = ["all", "archaeology", "cities", "food", "nature", "coast"];
const DESTS = DESTINATIONS.map((d) => d.key);

export default function ToursPage() {
  const { t } = useSite();
  const [params, setParams] = useSearchParams();
  const cat = CATS.includes(params.get("category")) ? params.get("category") : "all";
  const dest = DESTS.includes(params.get("destination")) ? params.get("destination") : "";
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).finally(() => setLoading(false));
  }, []);

  const setFilter = (next) => {
    const p = {};
    const c = next.category ?? cat;
    const d = next.destination ?? dest;
    if (c && c !== "all") p.category = c;
    if (d) p.destination = d;
    setParams(p);
  };

  const list = tours.filter((x) => (cat === "all" || x.category === cat) && (!dest || x.destination === dest));
  const visibleCats = CATS.filter((c) => c === "all" || tours.some((x) => x.category === c));
  const heroImg = dest ? DESTINATIONS.find((d) => d.key === dest)?.image : IMAGES.hero;
  const chip = (active) => `shrink-0 px-5 py-2.5 rounded-full text-sm border btn-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sea ${active ? "bg-ocean text-ivory border-ocean" : "bg-white text-ocean border-sand hover:border-sea"}`;

  return (
    <div data-testid="tours-page">
      <section className="relative h-[52vh] min-h-[400px] flex items-end overflow-hidden" data-testid="tours-hero">
        <img src={heroImg} alt={dest ? t(`dest.${dest}`) : "The Forum of Pompeii"} className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <Reveal className="relative max-w-6xl mx-auto w-full px-6 pb-14" key={dest || "all"}>
          <p className="text-xs font-medium tracking-super-wide text-sand-brand uppercase mb-4">{dest ? t("dest.eyebrow") : t("tours.eyebrow")}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-ivory font-normal leading-tight" data-testid="tours-title">{dest ? t(`dest.${dest}`) : t("tours.title")}</h1>
          <p className="mt-4 text-base md:text-lg text-ivory/85 max-w-2xl leading-relaxed">{dest ? t(`dest.${dest}D`) : t("tours.sub")}</p>
        </Reveal>
      </section>

      <section className="px-6 pb-28">
        <div className="max-w-6xl mx-auto">
          <div className="sticky top-20 z-20 bg-ivory/95 backdrop-blur-md -mx-6 px-6 py-5 border-b border-sand space-y-3">
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {visibleCats.map((c) => (
                <button key={c} data-testid={`tours-filter-${c}`} onClick={() => setFilter({ category: c })} aria-pressed={cat === c} className={chip(cat === c)}>
                  {t(`cat.${c}`)}
                </button>
              ))}
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar items-center">
              <span className="shrink-0 text-[11px] tracking-[0.2em] uppercase text-ocean/50 mr-1">{t("dest.filter")}</span>
              {DESTS.map((d) => (
                <button key={d} data-testid={`tours-dest-${d}`} onClick={() => setFilter({ destination: dest === d ? "" : d })} aria-pressed={dest === d}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs border btn-lift ${dest === d ? "bg-sea text-ivory border-sea" : "bg-transparent text-ocean border-sand hover:border-sea"}`}>
                  {t(`dest.${d}`)} {dest === d && <X size={12} />}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-10 text-xs tracking-[0.2em] uppercase text-sea" data-testid="tours-count">{t("tours.count", { n: list.length })}</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {loading
              ? [0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-sand/60 animate-pulse" />)
              : list.map((tour, i) => <TourCard key={tour.id} tour={tour} index={i} />)}
          </div>
          {!loading && list.length === 0 && (
            <div className="mt-10 bg-white rounded-2xl p-12 text-center shadow-soft" data-testid="tours-empty">
              <div className="brand-line mx-auto mb-6" />
              <p className="text-ocean/70">{t("tours.empty")}</p>
              <button onClick={() => setParams({})} className="mt-5 text-sm font-medium text-sea underline underline-offset-4" data-testid="tours-empty-clear">{t("tours.clear")}</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
